import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Mail,
  Users,
  FileText,
  Send,
  Plus,
  Trash2,
  Eye,
  Clock,
  CheckCircle2,
  Pencil,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/hooks/useAuth';
import {
  type EmailCampaign,
  type EmailSubscriber,
  type EmailTemplate,
  useCampaigns,
  useCreateCampaign,
  useCreateSubscriber,
  useCreateTemplate,
  useDeleteCampaign,
  useDeleteSubscriber,
  useDeleteTemplate,
  useSendCampaign,
  useSubscribers,
  useTemplates,
  useTestSendTemplate,
  useUpdateCampaign,
  useUpdateSubscriber,
  useUpdateTemplate,
} from '@/hooks/data/useEmailMarketing';
import {
  BRAND_TEMPLATES,
  PREVIEW_SAMPLE,
  renderSlots,
  type BrandEmailTemplate,
  type EmailTemplateSlot,
} from '@/lib/emailTemplates';
import { formatDate, formatNumber } from '@/lib/formatLocale';

export const Route = createFileRoute('/_app/email-marketing')({
  component: EmailMarketingPage,
});

function EmailMarketingPage() {
  const { t } = useTranslation();
  const { data: subs = [] } = useSubscribers();
  const { data: tpls = [] } = useTemplates();
  const { data: camps = [] } = useCampaigns();

  const stats = useMemo(() => {
    const activeSubs = subs.filter((s) => s.status === 'active').length;
    const sentCamps = camps.filter((c) => c.status === 'sent').length;
    const totalSent = camps.reduce((sum, c) => sum + c.sent_count, 0);
    const totalOpened = camps.reduce((sum, c) => sum + c.opened_count, 0);
    const openRate = totalSent > 0 ? Math.round((totalOpened / totalSent) * 100) : 0;
    return { activeSubs, totalSubs: subs.length, sentCamps, openRate };
  }, [subs, camps]);

  return (
    <section className="space-y-6">
      <header>
        <h2 className="font-display text-2xl text-ink">
          {t('email.title', 'E-Mail-Marketing')}
        </h2>
        <p className="text-sm text-ink-3">
          {t(
            'email.subtitle',
            'Abonnenten, Vorlagen und Kampagnen — alles über Resend.',
          )}
        </p>
      </header>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview" className="gap-2">
            <Mail className="h-3.5 w-3.5" /> {t('email.tab.overview', 'Übersicht')}
          </TabsTrigger>
          <TabsTrigger value="campaigns" className="gap-2">
            <Send className="h-3.5 w-3.5" /> {t('email.tab.campaigns', 'Kampagnen')}
          </TabsTrigger>
          <TabsTrigger value="templates" className="gap-2">
            <FileText className="h-3.5 w-3.5" /> {t('email.tab.templates', 'Vorlagen')}
          </TabsTrigger>
          <TabsTrigger value="subscribers" className="gap-2">
            <Users className="h-3.5 w-3.5" /> {t('email.tab.subscribers', 'Abonnenten')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <Overview stats={stats} subs={subs} camps={camps} />
        </TabsContent>
        <TabsContent value="campaigns">
          <CampaignsTab camps={camps} tpls={tpls} subsCount={stats.activeSubs} />
        </TabsContent>
        <TabsContent value="templates">
          <TemplatesTab tpls={tpls} />
        </TabsContent>
        <TabsContent value="subscribers">
          <SubscribersTab subs={subs} />
        </TabsContent>
      </Tabs>
    </section>
  );
}

// ───── Overview ─────
function Overview({
  stats,
  subs,
  camps,
}: {
  stats: { activeSubs: number; totalSubs: number; sentCamps: number; openRate: number };
  subs: EmailSubscriber[];
  camps: EmailCampaign[];
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KPI label={t('email.kpi.subscribers', 'Aktive Abonnenten')} value={stats.activeSubs} />
        <KPI label={t('email.kpi.total_subs', 'Gesamt')} value={stats.totalSubs} />
        <KPI label={t('email.kpi.sent', 'Gesendete Kampagnen')} value={stats.sentCamps} />
        <KPI label={t('email.kpi.open_rate', 'Open-Rate')} value={`${stats.openRate}%`} accent />
      </div>
      <div className="fm-card p-5">
        <h3 className="mb-3 font-display text-lg text-ink">
          {t('email.recent.title', 'Letzte Kampagnen')}
        </h3>
        {camps.length === 0 ? (
          <p className="text-sm text-ink-3">{t('email.recent.empty', 'Noch keine Kampagnen.')}</p>
        ) : (
          <ul className="space-y-2">
            {camps.slice(0, 5).map((c) => (
              <li
                key={c.id}
                className="flex items-center gap-3 rounded-md border border-line bg-paper-2 px-3 py-2"
              >
                <Mail className="h-4 w-4 text-ink-3" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink truncate">{c.name}</p>
                  <p className="text-xs text-ink-3">
                    {c.sent_at
                      ? `Gesendet ${formatDate(new Date(c.sent_at))}`
                      : 'Entwurf'}
                  </p>
                </div>
                <span className={`fm-pill ${campaignPill(c.status)}`}>{c.status}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <p className="text-xs text-ink-3">
        Latest subscriber:{' '}
        {subs.length > 0
          ? formatDate(new Date(subs[0]!.created_at))
          : '—'}
      </p>
    </div>
  );
}

// ───── Campaigns ─────
function CampaignsTab({
  camps,
  tpls,
  subsCount,
}: {
  camps: EmailCampaign[];
  tpls: EmailTemplate[];
  subsCount: number;
}) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { profile } = useAuth();
  const isAdmin = profile?.role === 'admin';
  const create = useCreateCampaign();
  const send = useSendCampaign();
  const del = useDeleteCampaign();
  const [creating, setCreating] = useState(false);

  const onSend = async (c: EmailCampaign) => {
    const ok = await confirm({
      title: t('email.confirm.send_title', 'Kampagne senden?'),
      body: `"${c.name}" → ${subsCount} ${t('email.subscribers_short', 'Abonnenten')}`,
      confirmLabel: t('email.send_now', 'Jetzt senden'),
    });
    if (!ok) return;
    try {
      const r = await send.mutateAsync(c.id);
      toast.success(t('email.campaign.send_result', '{{sent}} gesendet, {{failed}} fehlgeschlagen.', { sent: r.sent, failed: r.failed }));
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };

  const onDelete = async (c: EmailCampaign) => {
    const ok = await confirm({
      title: t('email.confirm.delete_campaign_title', 'Kampagne löschen?'),
      body: c.name,
      confirmLabel: t('common.delete', 'Löschen'),
      destructive: true,
    });
    if (!ok) return;
    try {
      await del.mutateAsync(c.id);
      toast.success(t('email.toast.deleted', 'Gelöscht.'));
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-ink-3">{camps.length} Kampagnen</p>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 rounded-md bg-ink px-3 py-2 text-sm font-semibold text-paper hover:bg-ink/90"
        >
          <Plus className="h-4 w-4" />
          {t('email.campaign.new', 'Neue Kampagne')}
        </button>
      </div>

      <div className="fm-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="fm-table">
            <thead>
              <tr>
                <th>{t('emailMarketingPage.col.name', 'Name')}</th>
                <th>{t('emailMarketingPage.col.status', 'Status')}</th>
                <th className="text-end">{t('emailMarketingPage.col.recipients', 'Empfänger')}</th>
                <th className="text-end">{t('emailMarketingPage.col.sent', 'Gesendet')}</th>
                <th className="text-end">{t('emailMarketingPage.col.opens', 'Öffnungen')}</th>
                <th>{t('emailMarketingPage.col.created', 'Erstellt')}</th>
                <th className="text-end">{t('emailMarketingPage.col.action', 'Aktion')}</th>
              </tr>
            </thead>
            <tbody>
              {camps.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-ink-3">
                    {t('email.campaign.empty', 'Keine Kampagnen.')}
                  </td>
                </tr>
              ) : (
                camps.map((c) => (
                  <tr key={c.id}>
                    <td className="font-medium text-ink">{c.name}</td>
                    <td>
                      <span className={`fm-pill ${campaignPill(c.status)}`}>{c.status}</span>
                    </td>
                    <td className="text-end">{c.recipients_count}</td>
                    <td className="text-end">{c.sent_count}</td>
                    <td className="text-end">{c.opened_count}</td>
                    <td className="text-xs text-ink-3">
                      {formatDate(new Date(c.created_at))}
                    </td>
                    <td>
                      <div className="flex justify-end gap-1">
                        {c.status === 'draft' && isAdmin && (
                          <button
                            type="button"
                            onClick={() => onSend(c)}
                            disabled={send.isPending}
                            title={t('email.send', 'Senden')}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-ink text-paper hover:bg-ink/90 disabled:opacity-50"
                          >
                            <Send className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            type="button"
                            onClick={() => onDelete(c)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger-bg))]"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CampaignDialog
        open={creating}
        onOpenChange={setCreating}
        templates={tpls}
        onCreate={async (name, templateId) => {
          await create.mutateAsync({
            name,
            templateId: templateId || null,
            status: 'draft',
          });
          setCreating(false);
          toast.success('Kampagne erstellt.');
        }}
      />
    </div>
  );
}

function CampaignDialog({
  open,
  onOpenChange,
  templates,
  onCreate,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  templates: EmailTemplate[];
  onCreate: (name: string, templateId: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [tpl, setTpl] = useState('');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display">
            {t('email.campaign.create', 'Neue Kampagne')}
          </DialogTitle>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            await onCreate(name.trim(), tpl);
            setName('');
            setTpl('');
          }}
        >
          <div className="space-y-1.5">
            <Label>{t('email.campaign.name', 'Name')} *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="space-y-1.5">
            <Label>{t('email.campaign.template', 'Vorlage')} *</Label>
            <select
              value={tpl}
              onChange={(e) => setTpl(e.target.value)}
              required
              className="w-full rounded-md border border-line bg-card px-3 py-2 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
            >
              <option value="">— wählen —</option>
              {templates.map((t2) => (
                <option key={t2.id} value={t2.id}>
                  {t2.name} — {t2.subject}
                </option>
              ))}
            </select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel', 'Abbrechen')}
            </Button>
            <Button type="submit" disabled={!name.trim() || !tpl}>
              {t('common.save', 'Speichern')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ───── Templates ─────
function TemplatesTab({ tpls }: { tpls: EmailTemplate[] }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const create = useCreateTemplate();
  const update = useUpdateTemplate();
  const del = useDeleteTemplate();
  const [editing, setEditing] = useState<EmailTemplate | null>(null);
  const [creating, setCreating] = useState(false);

  const onSave = async (input: Partial<EmailTemplate>, id?: string) => {
    try {
      if (id) await update.mutateAsync({ id, ...input });
      else await create.mutateAsync(input);
      toast.success('Gespeichert.');
      setEditing(null);
      setCreating(false);
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };

  const onDelete = async (t2: EmailTemplate) => {
    const ok = await confirm({
      title: t('email.confirm.delete_template_title', 'Vorlage löschen?'),
      body: t2.name,
      confirmLabel: t('common.delete', 'Löschen'),
      destructive: true,
    });
    if (!ok) return;
    try {
      await del.mutateAsync(t2.id);
      toast.success(t('email.toast.deleted', 'Gelöscht.'));
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-ink-3">{tpls.length} Vorlagen</p>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 rounded-md bg-ink px-3 py-2 text-sm font-semibold text-paper hover:bg-ink/90"
        >
          <Plus className="h-4 w-4" />
          {t('email.template.new', 'Neue Vorlage')}
        </button>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {tpls.length === 0 ? (
          <p className="md:col-span-2 py-8 text-center text-sm text-ink-3">
            {t('email.template.empty', 'Keine Vorlagen.')}
          </p>
        ) : (
          tpls.map((t2) => (
            <div key={t2.id} className="fm-card p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h4 className="font-display text-base text-ink">{t2.name}</h4>
                  <p className="text-xs text-ink-3">{t2.category ?? '—'}</p>
                </div>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setEditing(t2)}
                    className="inline-flex h-7 w-7 items-center justify-center rounded text-ink-2 hover:bg-paper-2"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(t2)}
                    className="inline-flex h-7 w-7 items-center justify-center rounded text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger-bg))]"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
              <p className="mt-2 text-sm text-ink-2 truncate">{t2.subject}</p>
              <p className="mt-1 line-clamp-2 text-xs text-ink-3">
                {t2.content.replace(/<[^>]+>/g, '').slice(0, 160)}
              </p>
            </div>
          ))
        )}
      </div>

      {(creating || editing) && (
        <TemplateDialog
          template={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSave={(input) => onSave(input, editing?.id)}
        />
      )}
    </div>
  );
}

// All slot tokens the brand templates reference. Used to render the chip row
// under the editor — each chip inserts `{slot}` at the active textarea/input
// caret so the makler doesn't have to remember the syntax.
const ALL_SLOTS: EmailTemplateSlot[] = [
  'first_name',
  'last_name',
  'client_name',
  'property_address',
  'property_price',
  'property_rooms',
  'property_m2',
  'property_district',
  'property_link',
  'viewing_date',
  'viewing_time',
  'agent_name',
  'agent_email',
  'agent_phone',
];

function TemplateDialog({
  template,
  onClose,
  onSave,
}: {
  template: EmailTemplate | null;
  onClose: () => void;
  onSave: (input: Partial<EmailTemplate>) => Promise<void>;
}) {
  const { t } = useTranslation();
  // Picker only shows for new templates. Editing an existing one jumps
  // straight into the editor.
  const [step, setStep] = useState<'pick' | 'edit'>(template ? 'edit' : 'pick');
  const [name, setName] = useState(template?.name ?? '');
  const [subject, setSubject] = useState(template?.subject ?? '');
  const [category, setCategory] = useState(template?.category ?? '');
  const [content, setContent] = useState(template?.content ?? '');
  const [showPreview, setShowPreview] = useState(false);

  const subjectRef = useRef<HTMLInputElement | null>(null);
  const contentRef = useRef<HTMLTextAreaElement | null>(null);
  const lastFocused = useRef<'subject' | 'content'>('content');

  const testSend = useTestSendTemplate();

  const choosePreset = (preset: BrandEmailTemplate) => {
    setName(preset.name);
    setSubject(preset.subject);
    setCategory(preset.category);
    setContent(preset.html);
    setStep('edit');
  };

  const startBlank = () => {
    setStep('edit');
  };

  const insertSlot = (slot: EmailTemplateSlot) => {
    const token = `{${slot}}`;
    if (lastFocused.current === 'subject') {
      const el = subjectRef.current;
      const pos = el?.selectionStart ?? subject.length;
      const next = subject.slice(0, pos) + token + subject.slice(pos);
      setSubject(next);
      requestAnimationFrame(() => {
        if (el) {
          el.focus();
          el.selectionStart = el.selectionEnd = pos + token.length;
        }
      });
    } else {
      const el = contentRef.current;
      const pos = el?.selectionStart ?? content.length;
      const next = content.slice(0, pos) + token + content.slice(pos);
      setContent(next);
      requestAnimationFrame(() => {
        if (el) {
          el.focus();
          el.selectionStart = el.selectionEnd = pos + token.length;
        }
      });
    }
  };

  const onTestSend = async () => {
    if (!subject.trim() || !content.trim()) {
      toast.error(t('email.template.test.missing', 'Betreff und Inhalt erforderlich.'));
      return;
    }
    try {
      const res = await testSend.mutateAsync({ subject, content });
      toast.success(
        t('email.template.test.sent', 'Test gesendet an {{to}}', { to: res.sent_to }),
      );
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };

  const previewHtml = useMemo(
    () => renderSlots(content, PREVIEW_SAMPLE),
    [content],
  );
  const previewSubject = useMemo(
    () => renderSlots(subject, PREVIEW_SAMPLE),
    [subject],
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="font-display">
            {template
              ? t('email.template.edit_title', 'Vorlage bearbeiten')
              : step === 'pick'
                ? t('email.template.pick_title', 'Vorlage wählen')
                : t('email.template.new', 'Neue Vorlage')}
          </DialogTitle>
        </DialogHeader>

        {step === 'pick' ? (
          <div className="space-y-4">
            <p className="text-sm text-ink-3">
              {t(
                'email.template.pick_hint',
                'Wählen Sie eine Brand-Vorlage als Ausgangspunkt — Sie können danach Inhalte beliebig anpassen.',
              )}
            </p>
            <div className="grid gap-3 md:grid-cols-2">
              {BRAND_TEMPLATES.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => choosePreset(preset)}
                  className="group rounded-xl border border-line bg-card p-4 text-start transition-all hover:border-[hsl(var(--brand-gold))] hover:shadow-soft"
                >
                  <p className="text-[11px] uppercase tracking-[0.1em] text-[hsl(var(--brand-gold-deep))]">
                    {preset.category}
                  </p>
                  <h4 className="mt-1 font-display text-base text-ink">{preset.name}</h4>
                  <p className="mt-2 text-sm text-ink-2">{preset.subject}</p>
                  <p className="mt-2 line-clamp-2 text-xs text-ink-3">
                    {preset.description}
                  </p>
                </button>
              ))}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                {t('common.cancel', 'Abbrechen')}
              </Button>
              <Button type="button" variant="ghost" onClick={startBlank}>
                {t('email.template.blank', 'Leer beginnen')}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              onSave({
                name: name.trim(),
                subject: subject.trim(),
                category: category.trim() || null,
                content,
              });
            }}
          >
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t('email.template.name', 'Name')} *</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div className="space-y-1.5">
                <Label>{t('email.template.category', 'Kategorie')}</Label>
                <Input
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder={t('email.template.category_placeholder', 'Termin / Marketing / Follow-up')}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t('email.template.subject', 'Betreff')} *</Label>
              <Input
                ref={subjectRef}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                onFocus={() => (lastFocused.current = 'subject')}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('email.template.content', 'Inhalt (HTML)')} *</Label>
              <Textarea
                ref={contentRef}
                rows={10}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                onFocus={() => (lastFocused.current = 'content')}
                required
                placeholder="<p>Sehr geehrte/r {first_name},</p>"
                className="font-mono text-xs"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.08em] text-ink-3">
                {t('email.template.slots', 'Platzhalter — Klick fügt im Cursor ein')}
              </Label>
              <div className="flex flex-wrap gap-1.5">
                {ALL_SLOTS.map((slot) => (
                  <button
                    key={slot}
                    type="button"
                    onClick={() => insertSlot(slot)}
                    className="inline-flex items-center rounded-full border border-line bg-paper-2 px-2.5 py-1 font-mono text-[11px] text-ink-2 transition-colors hover:border-[hsl(var(--brand-gold))] hover:text-ink"
                  >
                    {`{${slot}}`}
                  </button>
                ))}
              </div>
            </div>

            {showPreview && (
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.08em] text-ink-3">
                  {t('email.template.preview', 'Vorschau (mit Beispieldaten)')}
                </Label>
                <div className="rounded-md border border-line bg-paper-2 p-3">
                  <p className="mb-2 text-xs font-semibold text-ink-2">
                    {t('email.template.subject', 'Betreff')}: {previewSubject || '—'}
                  </p>
                  <iframe
                    title="email-preview"
                    srcDoc={previewHtml}
                    sandbox=""
                    className="h-[420px] w-full rounded border border-line bg-white"
                  />
                </div>
              </div>
            )}

            <DialogFooter className="flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                {t('common.cancel', 'Abbrechen')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowPreview((v) => !v)}
                disabled={!content.trim()}
              >
                <Eye className="me-1 h-3.5 w-3.5" />
                {showPreview
                  ? t('email.template.hide_preview', 'Vorschau ausblenden')
                  : t('email.template.show_preview', 'Vorschau anzeigen')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={onTestSend}
                disabled={testSend.isPending || !subject.trim() || !content.trim()}
              >
                <Send className="me-1 h-3.5 w-3.5" />
                {testSend.isPending
                  ? t('email.template.test.sending', 'Sende …')
                  : t('email.template.test.cta', 'Test an mich senden')}
              </Button>
              <Button
                type="submit"
                disabled={!name.trim() || !subject.trim() || !content.trim()}
              >
                {t('common.save', 'Speichern')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ───── Subscribers ─────
function SubscribersTab({ subs }: { subs: EmailSubscriber[] }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { profile } = useAuth();
  const isAdmin = profile?.role === 'admin';
  const create = useCreateSubscriber();
  const update = useUpdateSubscriber();
  const del = useDeleteSubscriber();
  const [adding, setAdding] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  const onAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await create.mutateAsync({ email: newEmail.trim() });
      toast.success(t('email.subscriber.added', 'Abonnent hinzugefügt.'));
      setNewEmail('');
      setAdding(false);
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };

  const onToggle = async (s: EmailSubscriber) => {
    try {
      await update.mutateAsync({
        id: s.id,
        status: s.status === 'active' ? 'unsubscribed' : 'active',
      });
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-ink-3">{subs.length} Abonnenten</p>
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-2 rounded-md bg-ink px-3 py-2 text-sm font-semibold text-paper hover:bg-ink/90"
        >
          <Plus className="h-4 w-4" />
          {t('email.subscriber.new', 'Hinzufügen')}
        </button>
      </div>

      <div className="fm-card overflow-hidden">
        <table className="fm-table">
          <thead>
            <tr>
              <th>E-Mail</th>
              <th>Status</th>
              <th>Hinzugefügt</th>
              <th className="text-end">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {subs.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center text-ink-3">
                  {t('email.subscriber.empty', 'Keine Abonnenten.')}
                </td>
              </tr>
            ) : (
              subs.map((s) => (
                <tr key={s.id}>
                  <td className="font-mono text-xs">{s.email}</td>
                  <td>
                    <span className={`fm-pill ${subscriberPill(s.status)}`}>
                      {s.status}
                    </span>
                  </td>
                  <td className="text-xs text-ink-3">
                    {formatDate(new Date(s.created_at))}
                  </td>
                  <td>
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => onToggle(s)}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-2 hover:bg-paper-2"
                        title={s.status === 'active' ? 'Deaktivieren' : 'Aktivieren'}
                      >
                        {s.status === 'active' ? (
                          <Eye className="h-3 w-3" />
                        ) : (
                          <CheckCircle2 className="h-3 w-3" />
                        )}
                      </button>
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={async () => {
                            const ok = await confirm({
                              title: t('email.confirm.delete_subscriber_title', 'Abonnent löschen?'),
                              body: s.email,
                              confirmLabel: t('common.delete', 'Löschen'),
                              destructive: true,
                            });
                            if (!ok) return;
                            try {
                              await del.mutateAsync(s.id);
                              toast.success(t('email.toast.deleted', 'Gelöscht.'));
                            } catch (cause) {
                              toast.error((cause as Error).message);
                            }
                          }}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger-bg))]"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {adding && (
        <Dialog open onOpenChange={(o) => !o && setAdding(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display">Neuer Abonnent</DialogTitle>
            </DialogHeader>
            <form className="space-y-4" onSubmit={onAdd}>
              <div className="space-y-1.5">
                <Label>E-Mail *</Label>
                <Input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  required
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setAdding(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" disabled={!newEmail.trim()}>
                  Hinzufügen
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ───── Helpers ─────
function KPI({
  label,
  value,
  accent,
}: {
  label: string;
  value: number | string;
  accent?: boolean;
}) {
  return (
    <div className={`fm-kpi ${accent ? '!border-ink !bg-ink' : ''}`}>
      <p className={`fm-kpi-label ${accent ? '!text-gold' : ''}`}>{label}</p>
      <p className={`fm-kpi-value ${accent ? '!text-paper' : ''}`}>
        {typeof value === 'number' ? formatNumber(value) : value}
      </p>
    </div>
  );
}

function campaignPill(status: EmailCampaign['status']): string {
  if (status === 'draft') return 'fm-pill-neutral';
  if (status === 'scheduled') return 'fm-pill-warn';
  if (status === 'sending') return 'fm-pill-info';
  if (status === 'sent') return 'fm-pill-pos';
  return 'fm-pill-neutral';
}

function subscriberPill(status: EmailSubscriber['status']): string {
  if (status === 'active') return 'fm-pill-pos';
  if (status === 'unsubscribed') return 'fm-pill-neutral';
  return 'fm-pill-dgr';
}
